import { NewPatientCalculationRule } from 'app/types/entities'

/**
 * New Patient Rules Mappings
 *
 * The PMS config UI presents a single multi-select dropdown for "Primary Rule",
 * but the backend stores these rules across multiple fields:
 *
 *   - Transaction-based rules (`all-procedures`, `new-patient-codes`, `ADA`) are
 *     stored in `settings.newPatientCalculation` as a single string value.
 *
 *   - Non-transaction rules (`first-completed-appointment`, `first-visit-date`)
 *     are stored as separate booleans: `settings.appointmentsSetNewPatient` and
 *     `settings.firstVisitSetNewPatient`.
 *
 * These functions translate between the unified UI selection and the split
 * backend representation. This split exists because transaction rules drive
 * `Transaction.afterSave` (real-time, per-transaction), while the boolean
 * rules drive independent calculations in `Contact.calculateBecamePatientDate`.
 *
 * The UI shows "First Eligible Procedure Transaction" which maps to either:
 *   - `ADA` when "Any ADA Code" is the only eligible procedure selected
 *   - `new-patient-codes` when specific codes are selected
 *
 * When ADA appears alongside active booleans and no explicit user selection,
 * it is treated as a beforeSave artifact (Location.beforeSave defaults to ADA
 * when no rules are set) and excluded from the selection.
 */

export const ANY_ADA_CODE = `any-ada-code`

interface MappedSettings {
  newPatientCalculation: NewPatientCalculationRule | undefined
  appointmentsSetNewPatient: boolean
  firstVisitSetNewPatient: boolean
}

/**
 * Translates UI multi-select values into backend settings fields for saving.
 *
 * At most one transaction rule is stored in `newPatientCalculation`.
 * When the user selects "First Eligible Procedure Transaction" with only
 * "Any ADA Code", we store `ADA`. With specific codes, we store `new-patient-codes`.
 * Boolean rules are stored independently.
 */
export function mapSelectionToSettings(selectedRules: string[], eligibleCodes?: string[]): MappedSettings {
  const hasAda = selectedRules.includes(`ADA`)
  const hasEligibleProcedure = selectedRules.includes(`new-patient-codes`)
  const hasAllProcedures = selectedRules.includes(`all-procedures`)

  let newPatientCalculation: NewPatientCalculationRule | undefined
  if (hasAllProcedures) {
    newPatientCalculation = `all-procedures`
  } else if (hasAda) {
    newPatientCalculation = `ADA`
  } else if (hasEligibleProcedure) {
    newPatientCalculation = `new-patient-codes`
  }

  return {
    newPatientCalculation,
    appointmentsSetNewPatient: selectedRules.includes(`first-completed-appointment`),
    firstVisitSetNewPatient: selectedRules.includes(`first-visit-date`),
  }
}

/**
 * Filters the eligible procedure codes from the UI form for persistence.
 *
 * - Strips out the `any-ada-code` sentinel — it is never persisted in newPatientCodes.
 *   ADA behavior is controlled by `newPatientCalculation: 'ADA'`.
 * - When undefined → returns undefined (don't overwrite existing value)
 */
export function filterEligibleProcedureCodesForSave(codes: string[] | undefined): string[] | undefined {
  if (codes === undefined) return undefined
  return codes.filter((c) => c !== ANY_ADA_CODE)
}

interface LocationSettings {
  newPatientCalculation?: string
  newPatientCodes?: string[]
  appointmentsSetNewPatient?: boolean
  firstVisitSetNewPatient?: boolean
}

/**
 * Translates backend settings fields into UI multi-select values for loading.
 *
 * ADA, new-patient-codes, and eligible-procedure all show as "First Eligible
 * Procedure Transaction" in the UI. The sub-dropdown (eligible procedure codes)
 * determines whether it's ADA or new-patient-codes on save.
 *
 * When ADA coexists with boolean rules, it is ignored (beforeSave artifact).
 * Defaults to all-procedures when no rules are configured.
 */
export function mapSettingsToSelection(settings: LocationSettings | undefined): string[] {
  const rules: string[] = []
  const npc = settings?.newPatientCalculation

  const hasBooleanRules = settings?.appointmentsSetNewPatient || settings?.firstVisitSetNewPatient

  if (npc === `ADA`) {
    if (hasBooleanRules && !settings?.newPatientCodes?.length) {
      // ADA was forced by Location.beforeSave default, not user-selected — skip it
    } else {
      rules.push(`ADA`)
    }
  } else if (npc === `new-patient-codes` || npc === `eligible-procedure`) {
    rules.push(`new-patient-codes`)
  } else if (npc === `all-procedures`) {
    rules.push(`all-procedures`)
  }

  if (settings?.appointmentsSetNewPatient) {
    rules.push(`first-completed-appointment`)
  }
  if (settings?.firstVisitSetNewPatient) {
    rules.push(`first-visit-date`)
  }

  return rules.length > 0 ? rules : [`all-procedures`]
}

/**
 * Maps backend settings to the eligible procedure codes UI state.
 * When newPatientCalculation is ADA with no specific codes, shows "Any ADA Code".
 * When newPatientCalculation is new-patient-codes, shows the stored codes.
 */
export function mapSettingsToEligibleCodes(settings: LocationSettings | undefined): string[] {
  const npc = settings?.newPatientCalculation
  if (npc === `ADA` || npc === `new-patient-codes` || npc === `eligible-procedure`) {
    return settings?.newPatientCodes?.length ? settings.newPatientCodes : []
  }
  return []
}
